import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import "chart.js/auto";
import { toast } from "react-toastify";
import Box from "../components/Box";
import "react-calendar/dist/Calendar.css";
import Sidebar from "../components/Sidebar";
import { Link } from "react-router-dom";
import AttendanceTable from "../components/dashboard/AttendanceTable";
import DashboardCharts from "../components/dashboard/DashboardCharts";
import CalendarPopup from "../components/dashboard/CalendarPopup";
import { 
  getAuthToken, 
  getDashboardData, 
  setDashboardData, 
  getChartData, 
  setChartData 
} from "../utils/cookieUtils";
import AccessModals from "../components/dashboard/AccessModals";
import { useDateAndAdmin } from "../utils/dateUtils";

function Dashboard() {
  const { currentDate, adminData, formattedDate, daysOfWeek, monthsOfYear } =
    useDateAndAdmin();

  const [totalRevenue, setTotalRevenue] = useState(0);
  const [statsData, setStatsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentWeekDates, setCurrentWeekDates] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [attendanceStatus, setAttendanceStatus] = useState(
    "No User has clocked-in today"
  );
  const [attendanceData, setAttendanceData] = useState([]);
  const [barChartData, setBarChartData] = useState(null);
  const [lineChartData, setLineChartData] = useState(null);
  const [currentMonthAttendance, setCurrentMonthAttendance] = useState(0);
  const [usersPerPage] = useState(10);
  const [membersChangeData, setMembersChangeData] = useState({
    totalMembersChange: "0",
    activeMembersChange: "0",
    inactiveMembersChange: "0",
    activeMembersChangeColor: "black",
    inactiveMembersChangeColor: "black",
  });

  const [currentFullMonthName, setCurrentFullMonthName] = useState("");
  const [number, setNumber] = useState("");
  const [grantAccess, setGrantAccess] = useState(false);
  const [clockInDetails, setClockInDetails] = useState({});
  const [clockInError, setClockInError] = useState("");
  const [adminProfile, setAdminProfile] = useState([]);

  const openOneTimeAccess = () => {
    setGrantAccess(true);
  };

  const closeOneTimeAccess = () => {
    setGrantAccess(false);
  };

  const fetchAdminProfileData = async () => {
    const token = getAuthToken();
    setLoading(true);
    try {
      const response = await axios.get("super-admin/get-admin-profile-data", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAdminProfile(response.data.message.admin);
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Failed to load admin profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminProfileData();
  }, []);

  const handleAccessSubmit = async (e) => {
    e.preventDefault();
    const token = getAuthToken();
    try {
      const response = await axios.post(
        "/super-admin/clock-in-user",
        { phone: number },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setClockInDetails(response.data.message.user);
      setGrantAccess(false);
      setAccessSuccess(true);
      setNumber("");
    } catch (error) {
      console.error(
        "Error:",
        error.response ? error.response.data : error.message
      );
      setNumber("");
      setGrantAccess(false);
      setClockInError(error.response.data.message);
      setAccessFailure(true);
    }
  };

  const [AccessSuccess, setAccessSuccess] = useState(false);
  const [AccessFailure, setAccessFailure] = useState(false);

  const AccessSuccessIsClosed = () => {
    setAccessSuccess(false);
  };

  const AccessFailureIsClosed = () => {
    setAccessFailure(false);
  };

  const fetchMonthlyTotalMembers = async () => {
    try {
      const token = getAuthToken();
      const currentDate = new Date();
      const currentMonthIndex = currentDate.getMonth();
      const previousMonthIndex =
        currentMonthIndex === 0 ? 11 : currentMonthIndex - 1;
      const yearForPreviousMonth =
        currentMonthIndex === 0
          ? currentDate.getFullYear() - 1
          : currentDate.getFullYear();

      const startOfCurrentMonth = new Date(
        currentDate.getFullYear(),
        currentMonthIndex,
        1
      );
      const endOfCurrentMonth = new Date(
        currentDate.getFullYear(),
        currentMonthIndex + 1,
        0
      );

      const startOfPreviousMonth = new Date(
        yearForPreviousMonth,
        previousMonthIndex,
        1
      );
      const endOfPreviousMonth = new Date(
        yearForPreviousMonth,
        previousMonthIndex + 1,
        0
      );

      const formattedStartOfCurrentMonth = startOfCurrentMonth
        .toISOString()
        .split("T")[0];
      const formattedEndOfCurrentMonth = endOfCurrentMonth
        .toISOString()
        .split("T")[0];

      const formattedStartOfPreviousMonth = startOfPreviousMonth
        .toISOString()
        .split("T")[0];
      const formattedEndOfPreviousMonth = endOfPreviousMonth
        .toISOString()
        .split("T")[0];

      const fetchMembersData = async (startDate, endDate) => {
        const response = await axios.get(`/super-admin/filter-total-members`, {
          params: { start_date: startDate, end_date: endDate },
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data.message;
      };

      const currentMonthData = await fetchMembersData(
        formattedStartOfCurrentMonth,
        formattedEndOfCurrentMonth
      );
      const previousMonthData = await fetchMembersData(
        formattedStartOfPreviousMonth,
        formattedEndOfPreviousMonth
      );

      const calculateStats = (data) => {
        const totalMembers = data.length || 0;
        const activeMembers =
          data.filter((user) => user.subscription_status === "subscribed")
            .length || 0;
        const inactiveMembers =
          data.filter((user) => user.subscription_status !== "subscribed")
            .length || 0;
        return { totalMembers, activeMembers, inactiveMembers };
      };

      const currentMonthStats = calculateStats(currentMonthData);
      const previousMonthStats = calculateStats(previousMonthData);

      const calculatePercentageChange = (current, previous) => {
        if (previous === 0) {
          return current === 0 ? 0 : 100;
        }
        return ((current - previous) / previous) * 100;
      };

      const totalMembersChange = calculatePercentageChange(
        currentMonthStats.totalMembers,
        previousMonthStats.totalMembers
      );
      const activeMembersChange = calculatePercentageChange(
        currentMonthStats.activeMembers,
        previousMonthStats.activeMembers
      );
      const inactiveMembersChange = calculatePercentageChange(
        currentMonthStats.inactiveMembers,
        previousMonthStats.inactiveMembers
      );

      const getChangeColor = (change) => (change >= 0 ? "blue" : "red");

      setMembersChangeData({
        totalMembersChange: totalMembersChange.toFixed(2),
        totalMembersChangeColor: getChangeColor(totalMembersChange),
        activeMembersChange: activeMembersChange.toFixed(2),
        activeMembersChangeColor: getChangeColor(activeMembersChange),
        inactiveMembersChange: inactiveMembersChange.toFixed(2),
        inactiveMembersChangeColor: getChangeColor(inactiveMembersChange),
      });
    } catch (error) {
      console.error("Error fetching monthly total members data:", error);
      setMembersChangeData({
        totalMembersChange: "N/A",
        activeMembersChange: "N/A",
        inactiveMembersChange: "N/A",
        totalMembersChangeColor: "black",
        activeMembersChangeColor: "black",
        inactiveMembersChangeColor: "black",
      });
    }
  };

  useEffect(() => {
    fetchMonthlyTotalMembers();
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      const cachedData = getDashboardData();

      if (cachedData) {
        setTotalRevenue(cachedData.totalRevenue);
        setStatsData(cachedData.statsData);
      }

      setLoading(true);
      const token = getAuthToken();
      const response = await axios.get("/super-admin/dashboard", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data.message;
      const stats = [
        {
          message: "Total Members",
          statNumber: data.total_members,
        },
        {
          message: "Active Members",
          statNumber: data.active_members,
        },
        {
          message: "Inactive Members",
          statNumber: data.inactive_members,
        },
      ];

      setTotalRevenue(data.total_revenue || 0);
      setStatsData(stats);
      setLoading(false);

      setDashboardData({
        totalRevenue: data.total_revenue || 0,
        statsData: stats,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError("Failed to fetch dashboard data");
      setLoading(false);
    }
  }, []);

  const updateWeekDates = useCallback(() => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    const weekDates = Array.from({ length: 7 }).map((_, i) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      return date;
    });
    setCurrentWeekDates(weekDates);
  }, [currentDate]);

  useEffect(() => {
    fetchDashboardData();
    updateWeekDates();
  }, []);

  const fetchAttendanceData = useCallback(async (startDate, endDate) => {
    const token = getAuthToken();
    setLoading(true);

    try {
      const response = await axios.get("/super-admin/filter-attendance", {
        params: {
          start_date: startDate.toLocaleDateString("en-CA"),
          end_date: endDate.toLocaleDateString("en-CA"),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const attendanceData = response.data?.message || [];
      setAttendanceData(attendanceData);

      setAttendanceStatus(
        attendanceData.length > 0 ? "" : "No user has clocked-in today"
      );
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setAttendanceStatus("Failed to fetch attendance data");
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAttendanceForDate = useCallback(
    async (date) => {
      setSelectedDate(date);
      await fetchAttendanceData(date, date);
    },
    [fetchAttendanceData]
  );

  useEffect(() => {
    loadAttendanceForDate(selectedDate);
  }, []);

  useEffect(() => {
    const fetchChartData = async () => {
      const token = getAuthToken();

      const cachedLineChartData = getChartData("lineChartData");
      const cachedBarChartData = getChartData("barChartData");

      if (cachedLineChartData && cachedBarChartData) {
        setLineChartData(cachedLineChartData);
        setBarChartData(cachedBarChartData);

        setCurrentMonthAttendance(
          cachedLineChartData.datasets[0].data[currentDate.getMonth()]
        );

        setCurrentFullMonthName(monthsOfYear[currentDate.getMonth()]);

        return;
      }

      // Fetch data if not cached
      const attendancePromises = monthsOfYear.map(async (_, monthIndex) => {
        const startOfMonth = new Date(currentDate.getFullYear(), monthIndex, 1);
        const endOfMonth = new Date(
          currentDate.getFullYear(),
          monthIndex + 1,
          0
        );
        const formattedStartDate = startOfMonth.toISOString().split("T")[0];
        const formattedEndDate = endOfMonth.toISOString().split("T")[0];

        try {
          const attendanceResponse = await axios.get(
            `/super-admin/filter-attendance?start_date=${formattedStartDate}&end_date=${formattedEndDate}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const attendanceResult = attendanceResponse.data.message;

          return attendanceResult.length;
        } catch (error) {
          console.error(
            `Error fetching attendance data for ${
              startOfMonth.getMonth() + 1
            }/${startOfMonth.getFullYear()}:`,
            error
          );
          return 0;
        }
      });

      const revenuePromises = monthsOfYear.map(async (_, monthIndex) => {
        const startOfMonth = new Date(currentDate.getFullYear(), monthIndex, 1);
        const endOfMonth = new Date(
          currentDate.getFullYear(),
          monthIndex + 1,
          0
        );
        const formattedStartDate = startOfMonth.toISOString().split("T")[0];
        const formattedEndDate = endOfMonth.toISOString().split("T")[0];

        try {
          const revenueResponse = await axios.get(
            `/super-admin/filter-revenue-sum?start_date=${formattedStartDate}&end_date=${formattedEndDate}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          return revenueResponse.data.message.revenue || 0;
        } catch (error) {
          console.error(
            `Error fetching revenue data for ${
              startOfMonth.getMonth() + 1
            }/${startOfMonth.getFullYear()}:`,
            error
          );
          return 0;
        }
      });

      try {
        const attendanceData2 = await Promise.all(attendancePromises);
        const revenueData = await Promise.all(revenuePromises);

        const lineChartData = {
          labels: monthsOfYear,
          datasets: [
            {
              label: "Number of members present",
              data: attendanceData2,
              backgroundColor: (context) => {
                const { chart } = context;
                const { ctx, chartArea } = chart;

                if (!chartArea) {
                  return null;
                }

                const gradient = ctx.createLinearGradient(
                  0,
                  chartArea.top,
                  0,
                  chartArea.bottom
                );
                gradient.addColorStop(0, "rgba(42, 111, 241, 0.4)");
                gradient.addColorStop(1, "rgba(42, 111, 241, 0)");

                return gradient;
              },
              borderColor: "rgba(42, 111, 241, 1)",
              borderWidth: 2,
              fill: true,
              tension: 0.4,
            },
          ],
        };

        const barChartData = {
          labels: monthsOfYear,
          datasets: [
            {
              label: "Generated Revenue",
              data: revenueData,
              backgroundColor: "rgba(42, 111, 241, 1)",
              borderColor: "rgba(54, 162, 235, 1)",
              borderWidth: 1,
              borderRadius: 10,
            },
          ],
        };

        setLineChartData(lineChartData);
        setBarChartData(barChartData);

        setChartData("lineChartData", lineChartData);
        setChartData("barChartData", barChartData);

        setCurrentMonthAttendance(attendanceData2[currentDate.getMonth()]);
        setCurrentFullMonthName(monthsOfYear[currentDate.getMonth()]);
      } catch (error) {
        console.error("Error fetching monthly data:", error);
      }
    };

    fetchChartData();
  }, [currentDate, monthsOfYear]);

  return (
    <div className="dashboard container-fluid">
      <div className="Nav">
        <Sidebar />
      </div>
      <div
        className="dashboard-display container-fluid"
      >
        <div className="top-bar container-fluid">
          <div className="top-bar-info">
            <p id="date">{formattedDate}</p>
            <b>
              <p>Welcome back {adminData}</p>
            </b>
          </div>
          <div className="top-bar-button">
            <button
              onClick={openOneTimeAccess}
              style={{
                background: "none",
                border: "2px solid #0473BD",
                color: "#0473BD",
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/access-control.png`}
                alt="Access control"
                style={{ height: "16px", width: "16px" }}
              />{" "}
              Provide Access
            </button>
            <Link to="/qr-code" target="_blank">
              <button>
                <img
                  src={`${process.env.PUBLIC_URL}/images/qr-button-img.png`}
                  alt="QR code"
                />{" "}
                Generate QR Code
              </button>
            </Link>
          </div>
        </div>
        <div className="stats-row">
          {statsData.map((stat, index) => (
            <Box
              key={index}
              message={stat.message}
              statNumber={stat.statNumber}
            />
          ))}
        </div>
        <div className="main-dashboard container-fluid">
          <DashboardCharts
            adminProfile={adminProfile}
            monthsOfYear={monthsOfYear}
          />
          <div className="daily-attendance container">
            <div className="attendance-header">
              <p>Daily Attendance</p>
              <button onClick={() => setShowCalendar(true)}>📅 Calendar</button>
            </div>
            <div className="week-dates">
              {currentWeekDates.map((date, index) => (
                <div
                  key={index}
                  className={`date-item ${
                    date.getDate() === selectedDate.getDate() ? "active" : ""
                  }`}
                  onClick={() => loadAttendanceForDate(date)}
                >
                  <b>
                    <p>{date.getDate()}</p>
                  </b>
                  <p className="date-item-days">{daysOfWeek[date.getDay()]}</p>
                </div>
              ))}
            </div>
            <AttendanceTable
              attendanceData={attendanceData}
              loading={loading}
              attendanceStatus={attendanceStatus}
            />
            <CalendarPopup
              showCalendar={showCalendar}
              setShowCalendar={setShowCalendar}
              onDateRangeSelect={(start, end) =>
                fetchAttendanceData(start, end)
              }
            />
          </div>
        </div>
      </div>
      <AccessModals
        grantAccess={grantAccess}
        closeOneTimeAccess={closeOneTimeAccess}
        handleAccessSubmit={handleAccessSubmit}
        number={number}
        setNumber={setNumber}
        setGrantAccess={setGrantAccess}
        AccessSuccess={AccessSuccess}
        AccessSuccessIsClosed={AccessSuccessIsClosed}
        clockInDetails={clockInDetails}
        AccessFailure={AccessFailure}
        AccessFailureIsClosed={AccessFailureIsClosed}
        clockInError={clockInError}
      />
    </div>
  );
}

export default Dashboard;
